import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Bus, Users, Clock, ArrowLeftRight } from "lucide-react";

interface BusPricingOption {
  rider_price: number;
  is_round_trip: boolean;
  duration_hours: number;
  passenger_count: number;
  user_friendly_name: string;
}

interface EventVehiclePricingProps {
  busPricingInfo: Record<string, BusPricingOption> | null;
  isLoading?: boolean;
}

export const EventVehiclePricing = ({ busPricingInfo, isLoading }: EventVehiclePricingProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card className="bg-white border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-gray-900">Available Vehicle Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="animate-pulse h-32 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!busPricingInfo || Object.keys(busPricingInfo).length === 0) {
    return null;
  }

  const pricingOptions = Object.entries(busPricingInfo).map(([key, option]) => ({
    key,
    ...option,
  }));

  return (
    <Card className="bg-white border border-gray-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-gray-900 flex items-center gap-2">
          <Bus className="h-5 w-5 text-green-600" />
          Available Vehicle Options
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pricingOptions.map((option) => (
            <div
              key={option.key}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <h4 className="font-semibold text-gray-900 text-sm mb-3">
                {option.user_friendly_name}
              </h4>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(option.rider_price)}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Users className="h-3 w-3" />
                  <span>Up to {option.passenger_count} passengers</span>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <Clock className="h-3 w-3" />
                  <span>{option.duration_hours} hour{option.duration_hours !== 1 ? 's' : ''}</span>
                </div>

                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <ArrowLeftRight className="h-3 w-3" />
                  <span>{option.is_round_trip ? 'Round Trip' : 'One Way'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
